import {sendEvent, type SendEventContext} from '@github-ui/hydro-analytics'
import {ActionMenu, ActionList, Label, type ButtonProps} from '@primer/react'
import {ArrowUpRightIcon, CopilotIcon, DownloadIcon, GearIcon, SpaceIcon} from '@primer/octicons-react'
import {COPILOT_CLI_DOCS_URL, CopilotDownloadMenuItems, recordMenuClick} from './EditorMenuItems'
import {publishOpenCopilotChat} from '@github-ui/copilot-chat-events'
import {CopilotChatIntents, CopilotLicenseType, CopilotPlan} from '@github-ui/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {useEntitlementData, usePlan} from './hooks/usePlan'
import {COPILOT_SPACES_PATH} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {GlobalCommands} from '@github-ui/ui-commands'
import {
  useExternalAnchor,
  type PropsWithPartialAnchor,
  type ReactPartialAnchorProps,
} from '@github-ui/react-core/react-partial-anchor'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useEffect, useState} from 'react'
import styles from './GlobalCopilotMenu.module.css'

const analyticsMenuLocation = 'global_copilot_menu'
const mode = 'global_nav'

const recordClick = (id: string, context: SendEventContext = {}) => {
  if (copilotFeatureFlags.freeToPaidTelemetry) {
    recordMenuClick({eventName: id, menuLocation: analyticsMenuLocation, mode})
  } else {
    sendEvent('dotcom_chat.activate', {
      target: `GLOBAL_COPILOT_MENU_${id}`,
      mode: 'global_nav',
      ...context,
    })
  }
}

function getWindowLocation() {
  if (!ssrSafeWindow) {
    throw new Error('Global Copilot menu navigation requires a browser environment')
  }

  return ssrSafeWindow.location
}

function redirectToSignup(returnTo: string) {
  const location = getWindowLocation()
  location.replace(`${location.origin}/github-copilot/signup?return_to=${encodeURIComponent(returnTo)}`)
}

const openAssistive = () => {
  publishOpenCopilotChat({
    id: 'copilot-assistive',
    intent: CopilotChatIntents.conversation,
    newThread: true,
    source: 'header',
  })
  recordClick('ASSISTIVE')
}

const openImmersive = () => {
  getWindowLocation().href = '/copilot'
  sendEvent('dotcom_chat.activate', {
    target: `GLOBAL_COPILOT_MENU_FULLSCREEN`,
    mode: 'global_nav',
  })
  recordClick('IMMERSIVE')
}

function ExternallyAnchoredGlobalCopilotMenu(props: PropsWithPartialAnchor<{}> & {defaultOpen?: boolean}) {
  const {ref: anchorRef, open, setOpen} = useExternalAnchor(props.reactPartialAnchor)

  useEffect(() => {
    if (props.defaultOpen) {
      setOpen(true)
    }
  }, [props.defaultOpen, setOpen])

  return (
    <ActionMenu anchorRef={anchorRef} open={open} onOpenChange={setOpen}>
      <GlobalCopilotMenuOverlay />
    </ActionMenu>
  )
}

export function GlobalCopilotMenuWithAnchor({
  anchorProps,
  defaultOpen,
}: {
  anchorProps?: ButtonProps
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen ?? false)
  return (
    <ActionMenu open={open} onOpenChange={setOpen}>
      <ActionMenu.Button leadingVisual={CopilotIcon} aria-label="Open Copilot…" {...anchorProps}>
        {''}
      </ActionMenu.Button>
      <GlobalCopilotMenuOverlay />
    </ActionMenu>
  )
}

export function GlobalCopilotMenu({
  reactPartialAnchor,
  anchorProps,
  defaultOpen,
}: ReactPartialAnchorProps & {anchorProps?: ButtonProps; defaultOpen?: boolean}) {
  return (
    <ErrorBoundary fallback={null}>
      <GlobalCommands
        commands={{
          'copilot-chat:open-assistive': openAssistive,
          'copilot-chat:open-immersive': openImmersive,
        }}
      />
      {reactPartialAnchor ? (
        <ExternallyAnchoredGlobalCopilotMenu reactPartialAnchor={reactPartialAnchor} defaultOpen={defaultOpen} />
      ) : (
        <GlobalCopilotMenuWithAnchor anchorProps={anchorProps} defaultOpen={defaultOpen} />
      )}
    </ErrorBoundary>
  )
}

function renderPlanType(plan: CopilotPlan | undefined) {
  switch (plan) {
    case CopilotPlan.IndividualProPlus:
      return <Label className={styles.proLabel}>Pro+</Label>
    case CopilotPlan.IndividualPro:
      return <Label className={styles.proLabel}>Pro</Label>
    case CopilotPlan.IndividualStudent:
      return <Label>Student</Label>
    case CopilotPlan.IndividualFree:
      return <Label>Free</Label>
    default:
      return null
  }
}

export function YourCopilotButton() {
  const plan = usePlan()

  return (
    <ActionList.LinkItem href="/settings/copilot" onClick={() => recordClick('SETTINGS')}>
      Your Copilot
      <ActionList.LeadingVisual>
        <CopilotIcon />
      </ActionList.LeadingVisual>
      {plan && <ActionList.TrailingVisual>{renderPlanType(plan)}</ActionList.TrailingVisual>}
    </ActionList.LinkItem>
  )
}

function GlobalCopilotMenuOverlay() {
  const {licenseType} = useEntitlementData()
  const isUnlicensed = licenseType === CopilotLicenseType.Unlicensed

  const handleAskCopilotClick = () => {
    recordClick('ASK_COPILOT_MENU_ITEM')
  }

  const handleSpacesClick = (e: React.MouseEvent) => {
    recordClick('SPACES')
    if (isUnlicensed) {
      e.preventDefault()
      redirectToSignup(COPILOT_SPACES_PATH)
    }
  }

  return (
    <ActionMenu.Overlay align="end">
      <ActionList style={{width: '15rem'}}>
        <ActionList.Group>
          <ActionList.GroupHeading>Ask Copilot…</ActionList.GroupHeading>
          <ActionList.LinkItem onClick={handleAskCopilotClick} href="/copilot">
            <ActionList.LeadingVisual>
              <ArrowUpRightIcon />
            </ActionList.LeadingVisual>
            In immersive chat
          </ActionList.LinkItem>
          <ActionList.LinkItem href={COPILOT_SPACES_PATH} onClick={handleSpacesClick}>
            <ActionList.LeadingVisual>
              <SpaceIcon />
            </ActionList.LeadingVisual>
            In a space
          </ActionList.LinkItem>
        </ActionList.Group>

        <ActionList.Divider />

        <ActionMenu onOpenChange={isOpen => isOpen && recordClick('IDE_MENU_OPEN')}>
          <ActionMenu.Anchor>
            <ActionList.Item onSelect={() => recordClick('OPEN_WITH')}>
              <ActionList.LeadingVisual>
                <DownloadIcon />
              </ActionList.LeadingVisual>
              Download for…
            </ActionList.Item>
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>
              <CopilotDownloadMenuItems
                cliHref={COPILOT_CLI_DOCS_URL}
                menuLocation={analyticsMenuLocation}
                mode={mode}
                onAppFallbackAnalyticsClick={() => recordClick('GITHUB_COPILOT_APP')}
                onCliFallbackAnalyticsClick={() => recordClick('CLI')}
              />
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>

        {copilotFeatureFlags.freeToPaidYourCopilotSettings ? (
          <YourCopilotButton />
        ) : (
          <ActionList.LinkItem href="/settings/copilot" onClick={() => recordClick('SETTINGS')}>
            Settings
            <ActionList.LeadingVisual>
              <GearIcon />
            </ActionList.LeadingVisual>
          </ActionList.LinkItem>
        )}
      </ActionList>
    </ActionMenu.Overlay>
  )
}
